import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, StickyNote, Lock } from "lucide-react";
import { fmtDateTime } from "@/lib/assignments";
import { authorTone } from "./WorkspaceTabs";

export default function NotesTab({ assignmentId, canManage, onChanged }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/assignments/${assignmentId}/notes`); setNotes(data); }
    catch { setNotes([]); }
  }, [assignmentId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/assignments/${assignmentId}/note`, { note: text.trim() });
      setText("");
      toast.success("Nota salvata");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio nota"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="notes-tab">
      {canManage && (
        <div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi una nota per la pratica… (autore, data e ora vengono registrati automaticamente)" rows={3} data-testid="note-input" />
          <div className="mt-2 flex items-center justify-between">
            <span className="flex items-center gap-1 text-[11px] text-slate-400"><Lock className="h-3 w-3" /> Le note salvate non sono modificabili né cancellabili</span>
            <Button size="sm" className="gap-1.5" disabled={busy || !text.trim()} onClick={save} data-testid="note-save">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <StickyNote className="h-3.5 w-3.5" />} Aggiungi nota
            </Button>
          </div>
        </div>
      )}
      {notes === null ? (
        <div className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-brand" /></div>
      ) : notes.length === 0 ? (
        <p className="py-4 text-center text-xs text-slate-400" data-testid="notes-empty">Nessuna nota ancora inserita.</p>
      ) : (
        <ol className="space-y-2" data-testid="notes-list">
          {notes.map((n) => (
            <li key={n.id} className={`rounded-md border border-slate-100 border-l-4 px-3 py-2 ${authorTone(n.author_id)}`} data-testid={`note-${n.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-x-2 text-[11px] text-slate-500">
                <span className="font-semibold text-slate-700">{n.author_name || "—"}{n.author_role ? ` · ${roleLabel(n.author_role)}` : ""}</span>
                <span>{fmtDateTime(n.created_at)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">{n.note}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export function roleLabel(r) {
  return r === "admin" ? "Direzione" : r === "backoffice" ? "Back Office" : r === "operatore" ? "Operatore" : r;
}
